import React, { useState } from 'react';
import { parseErrorMessage } from 'utils/index';
import PropTypes from 'prop-types';

/*
    Higher Order Component that separates out request and error handling state from component state
    for readability and a cleaner way to separate request errors from input errors (i.e. form validation).
    This is an optional approach. You can handle request errors in component state if you want. The goal here
    was to have separation of concerns.
*/
const withRequestErrors = (WrappedComponent) => {
    return (props) => {
        const [ requestErrors, setRequestErrors ] = useState(null);
        const [ isRequesting, setIsRequesting ] = useState(false);
        /*
            enhanceWithRequestErrors is a higher order function that takes action/api functions as an argument
            and returns a new function which will update error and requesting state for the wrapped component when called
            along with the original action/api call.

            First create the enhanced action function...
            const enhancedLoginAction = enhanceWithRequestErrors(actions.login);

            Now call the enhanced function as you normally would...

            enhancedLoginAction({
                ...this.state.user,
                password: sha224(this.state.user.password)
            })

            Alternatively, you can call the function without creating a new variable...
            enhanceWithRequestErrors(actions.login)({
                ...this.state.user,
                password: sha224(this.state.user.password)
            })

            Optionally, enhanceWithRequestErrors can take a callback function as a second argument
            if you want to modify the error response object. For instance, when you need to modify
            the error message for failed email signups to show which emails failed. By default the callback
            will simply set the error object as is.
        */

        const enhanceWithRequestErrors = (action, setErrorCb = error => error) => {
            return async (...args) => {
                try {
                    setIsRequesting(true);
                    setRequestErrors(null);
                    const response = await action(...args);
                    setIsRequesting(false);
                    return response;
                } catch (error) {
                    setIsRequesting(false);
                    setRequestErrors(setErrorCb(parseErrorMessage(error)));
                    throw error;
                }
            };
        };
        return (
            <WrappedComponent
                { ...props }
                setIsRequesting={ setIsRequesting }
                setRequestErrors={ setRequestErrors }
                enhanceWithRequestErrors={ enhanceWithRequestErrors }
                requestErrors={ requestErrors || {} }
                isRequesting={ isRequesting } />);
    };
};

export const withRequestErrorsPropTypes = {
    requestErrors: PropTypes.object,
    isRequesting: PropTypes.bool,
    enhanceWithRequestErrors: PropTypes.func,
    setIsRequesting: PropTypes.func,
    setRequestErrors: PropTypes.func
};

export default withRequestErrors;
